import { copy, defaultLanguage, type CopyKey, type Language } from './copy';
import { getCountryOptions, getCountryOptionsExceptFinland } from './countries';

interface GuestRegistrationGuest {
  lastName: string;
  firstNames: string;
  socialIdNumber: string;
  nationality: string;
  address: string;
  nationalityCode?: string;
  documentNumber?: string;
  documentType?: 'passport' | 'idCard';
  countryOfOrigin?: string;
  countryOfOriginCode?: string;
}

interface ClaimSession {
  token: string;
  channel: 'airbnb' | 'booking';
  checkInDate: string;
  checkOutDate: string;
  claimExpiresAtEpoch: number;
}

interface ApiIssue {
  path: string;
  message: string;
}

interface ApiErrorResponse {
  code?: string;
  message?: string;
  issues?: ApiIssue[];
}

interface ClaimResponse {
  claimToken: string;
  claimExpiresAtEpoch: number;
  reservation: {
    channel: ClaimSession['channel'];
    checkInDate: string;
    checkOutDate: string;
  };
  validation: {
    lastNameVerified: boolean;
    noteKey: string;
  };
}

interface InstructionsResponse {
  instructions: string;
}

interface LocalizedMessage {
  key: CopyKey;
  tone: 'error' | 'success' | 'info';
}

class ApiRequestError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;
  public readonly issues: ApiIssue[];

  public constructor(status: number, payload: ApiErrorResponse) {
    super(payload.message ?? 'Request failed.');
    this.status = status;
    this.code = payload.code;
    this.issues = payload.issues ?? [];
  }
}

const storageKey = 'bwob-checkin-session';
const languageStorageKey = 'bwob-checkin-language';

const claimForm = document.querySelector<HTMLFormElement>('#claim-form');
const registrationForm = document.querySelector<HTMLFormElement>('#registration-form');
const claimStep = document.querySelector<HTMLElement>('#claim-step');
const registrationStep = document.querySelector<HTMLElement>('#registration-step');
const completionStep = document.querySelector<HTMLElement>('#completion-step');
const statusBanner = document.querySelector<HTMLElement>('#status-banner');
const claimResult = document.querySelector<HTMLElement>('#claim-result');
const reservationSummary = document.querySelector<HTMLElement>('#reservation-summary');
const instructionsOutput = document.querySelector<HTMLElement>('#instructions-output');
const channelContext = document.querySelector<HTMLElement>('#channel-context');
const resetButton = document.querySelector<HTMLButtonElement>('#reset-flow');
const claimSubmitButton = document.querySelector<HTMLButtonElement>('#claim-submit');
const registrationSubmitButton = document.querySelector<HTMLButtonElement>('#registration-submit');
const languageButtons = document.querySelectorAll<HTMLButtonElement>('[data-language]');
const metaDescription = document.querySelector<HTMLMetaElement>('meta[name="description"]');
const numberOfGuestsInput = document.querySelector<HTMLSelectElement>('#numberOfGuests');
const guestFieldsContainer = document.querySelector<HTMLElement>('#guest-fields');

let currentLanguage: Language = defaultLanguage;
let currentSession: ClaimSession | undefined;
let currentClaimNoteKey: CopyKey | undefined;
let bannerMessage: LocalizedMessage | undefined;
let inlineMessage: LocalizedMessage | undefined;
let claimLoading = false;
let registrationLoading = false;
const detectedChannel = detectChannelFromHostname(window.location.hostname);
const finnishSocialIdChecksumChars = '0123456789ABCDEFHJKLMNPRSTUVWXY';

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function getFinnishCenturyPrefix(separator: string): string | undefined {
  if (separator === '+') {
    return '18';
  }

  if (separator === '-' || ['Y', 'X', 'W', 'V', 'U'].includes(separator)) {
    return '19';
  }

  if (['A', 'B', 'C', 'D', 'E', 'F'].includes(separator)) {
    return '20';
  }

  return undefined;
}

function isValidFinnishSocialId(value: string): boolean {
  const normalized = value.trim().toUpperCase();
  const match = normalized.match(/^(\d{2})(\d{2})(\d{2})([+\-ABCDEFUVWXY])(\d{3})([0-9A-Y])$/);
  if (!match) {
    return false;
  }

  const [, dayPart, monthPart, yearPart, rawSeparator, individualPart, checksum] = match;
  const separator = rawSeparator ?? '';
  const centuryPrefix = getFinnishCenturyPrefix(separator);
  if (!centuryPrefix) {
    return false;
  }

  const fullYear = Number(`${centuryPrefix}${yearPart}`);
  const month = Number(monthPart);
  const day = Number(dayPart);
  const individualNumber = Number(individualPart);

  if (individualNumber < 2) {
    return false;
  }

  const date = new Date(Date.UTC(fullYear, month - 1, day));
  if (
    Number.isNaN(date.getTime()) ||
    date.getUTCFullYear() !== fullYear ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return false;
  }

  const controlNumber = Number(`${dayPart}${monthPart}${yearPart}${individualPart}`);
  const expectedChecksum = finnishSocialIdChecksumChars[controlNumber % 31];

  return checksum === expectedChecksum;
}

function getStringFormValue(formData: FormData, fieldName: string): string {
  const value = formData.get(fieldName);
  return typeof value === 'string' ? value : '';
}

function isCopyKey(value: string): value is CopyKey {
  return value in copy.en;
}

function getStoredLanguage(): Language {
  const storedValue = localStorage.getItem(languageStorageKey);
  if (storedValue === 'fi' || storedValue === 'en') {
    return storedValue;
  }

  return navigator.language.toLowerCase().startsWith('fi') ? 'fi' : defaultLanguage;
}

function detectChannelFromHostname(hostname: string): ClaimSession['channel'] | undefined {
  const normalizedHost = hostname.toLowerCase();

  if (
    normalizedHost === 'localhost' ||
    normalizedHost === '127.0.0.1' ||
    normalizedHost.startsWith('localhost:') ||
    normalizedHost.startsWith('127.0.0.1:')
  ) {
    return 'airbnb';
  }

  if (normalizedHost === 'airbnb.bwob.vogon-it.com') {
    return 'airbnb';
  }

  if (normalizedHost === 'booking.bwob.vogon-it.com') {
    return 'booking';
  }

  return undefined;
}

function translate(key: CopyKey, variables?: Record<string, string>): string {
  const template = String(copy[currentLanguage][key] ?? copy[defaultLanguage][key]);
  if (!variables) {
    return template;
  }

  let translated = template;
  for (const [variable, value] of Object.entries(variables)) {
    translated = translated.replaceAll(`{${variable}}`, value);
  }

  return translated;
}

function renderMessage(element: HTMLElement | null, message: LocalizedMessage | undefined): void {
  if (!element) {
    return;
  }

  element.classList.remove('error', 'success', 'info');
  if (!message) {
    element.classList.add('hidden');
    element.textContent = '';
    return;
  }

  element.textContent = translate(message.key);
  element.classList.remove('hidden');
  element.classList.add(message.tone);
}

function setBanner(key: CopyKey, tone: LocalizedMessage['tone']): void {
  bannerMessage = { key, tone };
  renderMessage(statusBanner, bannerMessage);
}

function clearBanner(): void {
  bannerMessage = undefined;
  renderMessage(statusBanner, undefined);
}

function setInlineMessage(key: CopyKey, tone: LocalizedMessage['tone']): void {
  inlineMessage = { key, tone };
  renderMessage(claimResult, inlineMessage);
}

function clearInlineMessage(): void {
  inlineMessage = undefined;
  renderMessage(claimResult, undefined);
}

function renderReservationSummary(): void {
  if (!reservationSummary) {
    return;
  }

  if (!currentSession) {
    reservationSummary.classList.add('hidden');
    reservationSummary.textContent = '';
    return;
  }

  const channelKey: CopyKey = currentSession.channel === 'booking' ? 'claim.booking' : 'claim.airbnb';
  const lines = [
    translate('reservation.summary', {
      channel: translate(channelKey),
      checkIn: currentSession.checkInDate,
      checkOut: currentSession.checkOutDate,
    }),
  ];

  if (currentClaimNoteKey) {
    lines.push(translate(currentClaimNoteKey));
  }

  reservationSummary.textContent = lines.join(' ');
  reservationSummary.classList.remove('hidden');
}

function renderButtons(): void {
  if (claimSubmitButton) {
    claimSubmitButton.disabled = claimLoading;
    claimSubmitButton.textContent = translate(
      claimLoading ? 'claim.submit.loading' : 'claim.submit.idle',
    );
  }

  if (registrationSubmitButton) {
    registrationSubmitButton.disabled = registrationLoading;
    registrationSubmitButton.textContent = translate(
      registrationLoading ? 'register.submit.loading' : 'register.submit.idle',
    );
  }
}

function renderChannelContext(): void {
  if (!channelContext) {
    return;
  }

  if (!detectedChannel) {
    channelContext.textContent = '';
    channelContext.classList.add('hidden');
    return;
  }

  const key: CopyKey =
    detectedChannel === 'booking' ? 'claim.channelAuto.booking' : 'claim.channelAuto.airbnb';
  channelContext.textContent = translate(key);
  channelContext.classList.remove('hidden');
}

function setLanguage(language: Language): void {
  currentLanguage = language;
  localStorage.setItem(languageStorageKey, language);

  document.documentElement.lang = language;
  document.title = translate('meta.title');
  if (metaDescription) {
    metaDescription.content = translate('meta.description');
  }

  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((element) => {
    const key = element.dataset.i18n;
    if (!key || !isCopyKey(key)) {
      return;
    }

    element.textContent = translate(key);
  });

  languageButtons.forEach((button) => {
    button.classList.toggle('active', button.dataset.language === language);
  });

  renderButtons();
  renderChannelContext();
  renderReservationSummary();
  renderGuestFields();
  renderMessage(statusBanner, bannerMessage);
  renderMessage(claimResult, inlineMessage);
}

function getSelectedGuestCount(): number {
  const parsed = Number(numberOfGuestsInput?.value ?? '1');
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 4 ? parsed : 1;
}

function getGuestFieldName(index: number, field: keyof GuestRegistrationGuest): string {
  return `guest-${index}-${field}`;
}

function getGuestFieldValue(formData: FormData, index: number, field: keyof GuestRegistrationGuest): string {
  return getStringFormValue(formData, getGuestFieldName(index, field));
}

function renderCountryOptions(excludeFinland = false, selectedCode = ''): string {
  const options = excludeFinland
    ? getCountryOptionsExceptFinland(currentLanguage)
    : getCountryOptions(currentLanguage);

  return [
    '<option value=""></option>',
    ...options.map(
      (country) =>
        `<option value="${country.code}" ${country.code === selectedCode ? 'selected' : ''}>${escapeHtml(country.name)}</option>`,
    ),
  ].join('');
}

function renderGuestFields(): void {
  if (!guestFieldsContainer) {
    return;
  }

  const count = getSelectedGuestCount();
  const snapshot = registrationForm ? new FormData(registrationForm) : undefined;

  guestFieldsContainer.innerHTML = Array.from({ length: count }, (_, index) => {
    const guestIndex = index + 1;
    const nationalityCode = getGuestFieldValue(snapshot ?? new FormData(), guestIndex, 'nationalityCode');
    const nationalityLabel = nationalityCode
      ? getCountryOptions(currentLanguage).find((country) => country.code === nationalityCode)?.name ?? ''
      : '';
    const showTravelFields = nationalityCode ? nationalityCode !== 'FI' : false;
    const countryOfOriginCode = getGuestFieldValue(snapshot ?? new FormData(), guestIndex, 'countryOfOriginCode');
    const documentType = getGuestFieldValue(snapshot ?? new FormData(), guestIndex, 'documentType');

    return `
      <section class="guest-card">
        <h3 class="guest-card-title">${escapeHtml(translate('register.guestCardTitle', { index: String(guestIndex) }))}</h3>
        <div class="field-grid">
          <label class="field">
            <span>${escapeHtml(translate('register.lastName'))}</span>
            <small class="field-help">${escapeHtml(translate('register.lastNameHelp'))}</small>
            <input name="${getGuestFieldName(guestIndex, 'lastName')}" type="text" value="${escapeHtml(getGuestFieldValue(snapshot ?? new FormData(), guestIndex, 'lastName'))}" required />
          </label>

          <label class="field">
            <span>${escapeHtml(translate('register.firstNames'))}</span>
            <small class="field-help">${escapeHtml(translate('register.firstNamesHelp'))}</small>
            <input name="${getGuestFieldName(guestIndex, 'firstNames')}" type="text" value="${escapeHtml(getGuestFieldValue(snapshot ?? new FormData(), guestIndex, 'firstNames'))}" required />
          </label>

          <label class="field">
            <span>${escapeHtml(translate('register.socialIdNumber'))}</span>
            <small class="field-help">${escapeHtml(translate('register.socialIdNumberHelp'))}</small>
            <input name="${getGuestFieldName(guestIndex, 'socialIdNumber')}" data-guest-index="${guestIndex}" data-social-id-field="true" type="text" value="${escapeHtml(getGuestFieldValue(snapshot ?? new FormData(), guestIndex, 'socialIdNumber'))}" required />
          </label>

          <label class="field">
            <span>${escapeHtml(translate('register.nationality'))}</span>
            <small class="field-help">${escapeHtml(translate('register.nationalityHelp'))}</small>
            <select name="${getGuestFieldName(guestIndex, 'nationalityCode')}" data-guest-index="${guestIndex}" data-nationality-field="true" required>
              ${renderCountryOptions(false, nationalityCode)}
            </select>
            <input name="${getGuestFieldName(guestIndex, 'nationality')}" type="hidden" value="${escapeHtml(nationalityLabel)}" />
          </label>

          <label class="field field-wide">
            <span>${escapeHtml(translate('register.address'))}</span>
            <small class="field-help">${escapeHtml(translate('register.addressHelp'))}</small>
            <textarea name="${getGuestFieldName(guestIndex, 'address')}" rows="2" required>${escapeHtml(getGuestFieldValue(snapshot ?? new FormData(), guestIndex, 'address'))}</textarea>
          </label>

          <label class="field ${showTravelFields ? '' : 'hidden'}" data-guest-index="${guestIndex}" data-travel-field="documentNumber">
            <span>${escapeHtml(translate('register.documentNumber'))}</span>
            <small class="field-help">${escapeHtml(translate('register.documentNumberHelp'))}</small>
            <input name="${getGuestFieldName(guestIndex, 'documentNumber')}" type="text" value="${escapeHtml(getGuestFieldValue(snapshot ?? new FormData(), guestIndex, 'documentNumber'))}" ${showTravelFields ? 'required' : ''} />
          </label>

          <label class="field ${showTravelFields ? '' : 'hidden'}" data-guest-index="${guestIndex}" data-travel-field="documentType">
            <span>Passport or ID card</span>
            <small class="field-help">Choose whether the provided document number is from a passport or an ID card.</small>
            <select name="${getGuestFieldName(guestIndex, 'documentType')}" ${showTravelFields ? 'required' : ''}>
              <option value=""></option>
              <option value="passport" ${documentType === 'passport' ? 'selected' : ''}>Passport</option>
              <option value="idCard" ${documentType === 'idCard' ? 'selected' : ''}>ID card</option>
            </select>
          </label>

          <label class="field ${showTravelFields ? '' : 'hidden'}" data-guest-index="${guestIndex}" data-travel-field="countryOfOrigin">
            <span>${escapeHtml(translate('register.countryOfOrigin'))}</span>
            <small class="field-help">${escapeHtml(translate('register.countryOfOriginHelp'))}</small>
            <select name="${getGuestFieldName(guestIndex, 'countryOfOriginCode')}" ${showTravelFields ? 'required' : ''}>
              ${renderCountryOptions(true, countryOfOriginCode)}
            </select>
            <input name="${getGuestFieldName(guestIndex, 'countryOfOrigin')}" type="hidden" value="${escapeHtml(
              countryOfOriginCode
                ? getCountryOptionsExceptFinland(currentLanguage).find((country) => country.code === countryOfOriginCode)?.name ?? ''
                : '',
            )}" />
          </label>
        </div>
      </section>
    `;
  }).join('');
}

function updateGuestTravelFields(guestIndex: number, nationalityCode: string): void {
  const showTravelFields = nationalityCode !== 'FI' && nationalityCode !== '';
  const nationalityInput = document.querySelector<HTMLInputElement>(
    `[name="${getGuestFieldName(guestIndex, 'nationality')}"]`,
  );
  if (nationalityInput) {
    nationalityInput.value = nationalityCode
      ? getCountryOptions(currentLanguage).find((country) => country.code === nationalityCode)?.name ?? ''
      : '';
  }

  document
    .querySelectorAll<HTMLElement>(`[data-guest-index="${guestIndex}"][data-travel-field]`)
    .forEach((element) => {
      element.classList.toggle('hidden', !showTravelFields);
      const control = element.querySelector<HTMLInputElement | HTMLSelectElement>('input, select');
      if (!control) {
        return;
      }

      control.required = showTravelFields;
      if (!showTravelFields) {
        control.value = '';
      }
    });

  const countryOfOriginInput = document.querySelector<HTMLInputElement>(
    `[name="${getGuestFieldName(guestIndex, 'countryOfOrigin')}"]`,
  );
  const countryOfOriginCodeControl = document.querySelector<HTMLSelectElement>(
    `[name="${getGuestFieldName(guestIndex, 'countryOfOriginCode')}"]`,
  );
  if (countryOfOriginInput && countryOfOriginCodeControl) {
    countryOfOriginInput.value = countryOfOriginCodeControl.value
      ? getCountryOptionsExceptFinland(currentLanguage).find((country) => country.code === countryOfOriginCodeControl.value)?.name ?? ''
      : '';
  }

  validateFinnishSocialIdField(guestIndex, false);
}

function validateFinnishSocialIdField(guestIndex: number, shouldReport: boolean): boolean {
  const nationalityControl = document.querySelector<HTMLSelectElement>(
    `[name="${getGuestFieldName(guestIndex, 'nationalityCode')}"]`,
  );
  const socialIdControl = document.querySelector<HTMLInputElement>(
    `[name="${getGuestFieldName(guestIndex, 'socialIdNumber')}"]`,
  );

  if (!socialIdControl) {
    return true;
  }

  if (nationalityControl?.value !== 'FI') {
    socialIdControl.setCustomValidity('');
    socialIdControl.classList.remove('field-input-invalid');
    return true;
  }

  const value = socialIdControl.value.trim();
  if (!value) {
    socialIdControl.setCustomValidity('');
    socialIdControl.classList.remove('field-input-invalid');
    return true;
  }

  const isValid = isValidFinnishSocialId(value);
  socialIdControl.setCustomValidity(isValid ? '' : translate('errors.invalidFinnishSocialId'));
  socialIdControl.classList.toggle('field-input-invalid', !isValid);

  if (shouldReport && !isValid) {
    socialIdControl.reportValidity();
  }

  return isValid;
}

function saveSession(session: ClaimSession): void {
  localStorage.setItem(storageKey, JSON.stringify(session));
}

function loadSession(): ClaimSession | undefined {
  const raw = localStorage.getItem(storageKey);
  if (!raw) {
    return undefined;
  }

  try {
    return JSON.parse(raw) as ClaimSession;
  } catch {
    localStorage.removeItem(storageKey);
    return undefined;
  }
}

function clearSession(): void {
  localStorage.removeItem(storageKey);
}

async function parseResponse<T>(response: Response): Promise<T> {
  const data = (await response.json()) as T | ApiErrorResponse;
  if (!response.ok) {
    throw new ApiRequestError(response.status, data as ApiErrorResponse);
  }

  return data as T;
}

function showRegistrationStep(session: ClaimSession, validationNoteKey?: CopyKey): void {
  currentSession = session;
  currentClaimNoteKey = validationNoteKey;
  claimStep?.classList.remove('hidden');
  registrationStep?.classList.remove('hidden');
  completionStep?.classList.add('hidden');
  renderReservationSummary();
}

function showCompletionStep(instructions: string): void {
  claimStep?.classList.add('hidden');
  registrationStep?.classList.add('hidden');
  completionStep?.classList.remove('hidden');

  if (instructionsOutput) {
    instructionsOutput.textContent = instructions;
  }
}

function resetFlow(): void {
  currentSession = undefined;
  currentClaimNoteKey = undefined;
  clearSession();
  claimForm?.reset();
  registrationForm?.reset();
  if (numberOfGuestsInput) {
    numberOfGuestsInput.value = '1';
  }
  renderGuestFields();
  clearBanner();
  clearInlineMessage();
  claimStep?.classList.remove('hidden');
  registrationStep?.classList.add('hidden');
  completionStep?.classList.add('hidden');
  if (instructionsOutput) {
    instructionsOutput.textContent = '';
  }
  renderReservationSummary();
}

function isValidIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00.000Z`));
}

function validateClaim(payload: {
  checkInDate: string;
  checkOutDate: string;
  lastName: string;
}): CopyKey | undefined {
  if (!payload.lastName || !payload.checkInDate || !payload.checkOutDate) {
    return 'errors.claimMissingFields';
  }

  if (!isValidIsoDate(payload.checkInDate) || !isValidIsoDate(payload.checkOutDate)) {
    return 'errors.invalidDateRange';
  }

  if (Date.parse(`${payload.checkOutDate}T00:00:00.000Z`) <= Date.parse(`${payload.checkInDate}T00:00:00.000Z`)) {
    return 'errors.invalidDateRange';
  }

  return undefined;
}

function validateRegistration(payload: {
  email: string;
  phoneNumber: string;
  numberOfGuests: number;
  guests: GuestRegistrationGuest[];
}): CopyKey | undefined {
  if (!payload.email || !payload.phoneNumber) {
    return 'errors.registrationMissingFields';
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
    return 'errors.invalidEmail';
  }

  if (!Number.isInteger(payload.numberOfGuests) || payload.numberOfGuests < 1 || payload.numberOfGuests > 4) {
    return 'errors.invalidGuestCount';
  }

  if (payload.guests.length !== payload.numberOfGuests) {
    return 'errors.invalidGuestCount';
  }

  for (const guest of payload.guests) {
    if (
      !guest.lastName ||
      !guest.firstNames ||
      !guest.socialIdNumber ||
      !guest.nationality ||
      !guest.nationalityCode ||
      !guest.address
    ) {
      return 'errors.registrationMissingFields';
    }

    if (guest.nationalityCode !== 'FI' && (!guest.documentNumber || !guest.documentType || !guest.countryOfOrigin || !guest.countryOfOriginCode)) {
      return 'errors.registrationMissingFields';
    }

    if (guest.nationalityCode === 'FI' && !isValidFinnishSocialId(guest.socialIdNumber)) {
      return 'errors.invalidFinnishSocialId';
    }
  }

  if (payload.guests.some((guest) => guest.address.length < 5)) {
    return 'errors.registrationMissingFields';
  }

  return undefined;
}

function mapValidationIssueKey(issues: ApiIssue[]): CopyKey {
  const issuePaths = issues.map((issue) => issue.path);
  if (issuePaths.some((path) => path.includes('email'))) {
    return 'errors.invalidEmail';
  }

  if (issuePaths.some((path) => path.includes('socialIdNumber'))) {
    return 'errors.invalidFinnishSocialId';
  }

  if (issuePaths.some((path) => path.includes('numberOfGuests'))) {
    return 'errors.invalidGuestCount';
  }

  if (issuePaths.some((path) => path.includes('guests'))) {
    return 'errors.registrationMissingFields';
  }

  return 'errors.validationFailed';
}

function mapApiErrorKey(error: unknown, fallbackKey: CopyKey): CopyKey {
  if (!(error instanceof ApiRequestError)) {
    return fallbackKey;
  }

  if (error.code === 'validation_failed') {
    return mapValidationIssueKey(error.issues);
  }

  const errorMap: Record<string, CopyKey> = {
    route_not_found: 'errors.routeNotFound',
    request_body_required: 'errors.requestBodyRequired',
    invalid_json: 'errors.invalidJson',
    missing_query_parameter: 'errors.missingQueryParameter',
    reservation_not_found: 'errors.reservationNotFound',
    registration_completed: 'errors.registrationCompleted',
    last_name_mismatch: 'errors.lastNameMismatch',
    reservation_locked: 'errors.reservationLocked',
    claim_token_invalid: 'errors.claimTokenInvalid',
    claim_token_expired: 'errors.claimTokenExpired',
    registration_not_completed: 'errors.registrationNotCompleted',
    invalid_date_range: 'errors.invalidDateRange',
    temporary_service_error: 'errors.temporaryServiceError',
  };

  return error.code ? (errorMap[error.code] ?? fallbackKey) : fallbackKey;
}

async function restoreSessionIfPossible(): Promise<void> {
  const session = loadSession();
  if (!session) {
    return;
  }

  if (session.claimExpiresAtEpoch < Math.floor(Date.now() / 1000)) {
    clearSession();
    return;
  }

  try {
    const response = await fetch(`/api/instructions?token=${encodeURIComponent(session.token)}`, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    });

    if (response.status === 409) {
      showRegistrationStep(session);
      return;
    }

    const data = await parseResponse<InstructionsResponse>(response);
    showCompletionStep(data.instructions);
    currentSession = session;
    setBanner('banner.sessionRestored', 'success');
  } catch {
    clearSession();
  }
}

claimForm?.addEventListener('submit', async (event) => {
  event.preventDefault();
  clearBanner();
  clearInlineMessage();

  const formData = new FormData(claimForm);
  const payload = {
    channel: detectedChannel,
    checkInDate: getStringFormValue(formData, 'checkInDate'),
    checkOutDate: getStringFormValue(formData, 'checkOutDate'),
    lastName: getStringFormValue(formData, 'lastName').trim(),
  };

  if (!payload.channel) {
    setInlineMessage('errors.unknownChannelHost', 'error');
    return;
  }

  const claimValidationError = validateClaim(payload);
  if (claimValidationError) {
    setInlineMessage(claimValidationError, 'error');
    return;
  }

  claimLoading = true;
  renderButtons();
  setInlineMessage('banner.claimLoading', 'info');

  try {
    const response = await fetch('/api/claim', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await parseResponse<ClaimResponse>(response);

    const session: ClaimSession = {
      token: data.claimToken,
      channel: data.reservation.channel,
      checkInDate: data.reservation.checkInDate,
      checkOutDate: data.reservation.checkOutDate,
      claimExpiresAtEpoch: data.claimExpiresAtEpoch,
    };

    saveSession(session);
    const noteKey = isCopyKey(data.validation.noteKey)
      ? data.validation.noteKey
      : 'reservation.note.dateRangeOnly';
    showRegistrationStep(session, noteKey);
    setInlineMessage(noteKey, 'success');
  } catch (error: unknown) {
    setInlineMessage(mapApiErrorKey(error, 'errors.genericRequest'), 'error');
  } finally {
    claimLoading = false;
    renderButtons();
  }
});

registrationForm?.addEventListener('submit', async (event) => {
  event.preventDefault();
  clearBanner();

  const session = loadSession();
  if (!session) {
    setBanner('banner.claimRequired', 'error');
    return;
  }

  currentSession = session;

  const formData = new FormData(registrationForm);
  const numberOfGuests = Number(getStringFormValue(formData, 'numberOfGuests'));
  const guests: GuestRegistrationGuest[] = Array.from({ length: numberOfGuests }, (_, index) => {
    const guestIndex = index + 1;
    const nationalityCode = getGuestFieldValue(formData, guestIndex, 'nationalityCode').trim();
    const nationality = getGuestFieldValue(formData, guestIndex, 'nationality').trim();
    const documentNumber = getGuestFieldValue(formData, guestIndex, 'documentNumber').trim();
    const documentTypeValue = getGuestFieldValue(formData, guestIndex, 'documentType').trim();
    const documentType: 'passport' | 'idCard' | undefined =
      documentTypeValue === 'passport' || documentTypeValue === 'idCard'
        ? documentTypeValue
        : undefined;
    const countryOfOrigin = getGuestFieldValue(formData, guestIndex, 'countryOfOrigin').trim();
    const countryOfOriginCode = getGuestFieldValue(formData, guestIndex, 'countryOfOriginCode').trim();

    return {
      lastName: getGuestFieldValue(formData, guestIndex, 'lastName').trim(),
      firstNames: getGuestFieldValue(formData, guestIndex, 'firstNames').trim(),
      socialIdNumber: getGuestFieldValue(formData, guestIndex, 'socialIdNumber').trim(),
      nationality,
      ...(nationalityCode ? { nationalityCode } : {}),
      address: getGuestFieldValue(formData, guestIndex, 'address').trim(),
      ...(nationalityCode !== 'FI' && documentNumber ? { documentNumber } : {}),
      ...(nationalityCode !== 'FI' && documentType ? { documentType } : {}),
      ...(nationalityCode !== 'FI' && countryOfOrigin ? { countryOfOrigin } : {}),
      ...(nationalityCode !== 'FI' && countryOfOriginCode ? { countryOfOriginCode } : {}),
    };
  });

  const payload = {
    claimToken: session.token,
    registration: {
      numberOfGuests,
      email: getStringFormValue(formData, 'email').trim(),
      phoneNumber: getStringFormValue(formData, 'phoneNumber').trim(),
      guests,
      confirmedCorrectness: Boolean(formData.get('confirmedCorrectness')),
    },
  };

  const registrationValidationError = validateRegistration(payload.registration);
  if (registrationValidationError) {
    setBanner(registrationValidationError, 'error');
    return;
  }

  if (!payload.registration.confirmedCorrectness) {
    setBanner('banner.confirmationRequired', 'error');
    return;
  }

  registrationLoading = true;
  renderButtons();
  setBanner('banner.registrationLoading', 'info');

  try {
    const response = await fetch('/api/register', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await parseResponse<InstructionsResponse>(response);
    showCompletionStep(data.instructions);
    setBanner('banner.registrationComplete', 'success');
  } catch (error: unknown) {
    setBanner(mapApiErrorKey(error, 'errors.genericSubmission'), 'error');
  } finally {
    registrationLoading = false;
    renderButtons();
  }
});

resetButton?.addEventListener('click', () => {
  resetFlow();
});

numberOfGuestsInput?.addEventListener('change', () => {
  renderGuestFields();
});

guestFieldsContainer?.addEventListener('input', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement) || target.dataset.nationalityField !== 'true') {
    return;
  }

  const guestIndex = Number(target.dataset.guestIndex ?? '0');
  if (!guestIndex) {
    return;
  }

  updateGuestTravelFields(guestIndex, target.value);
});

guestFieldsContainer?.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLSelectElement)) {
    return;
  }

  if (target.dataset.nationalityField === 'true') {
    const guestIndex = Number(target.dataset.guestIndex ?? '0');
    if (!guestIndex) {
      return;
    }

    updateGuestTravelFields(guestIndex, target.value);
    return;
  }

  if (target.name.includes('countryOfOriginCode')) {
    const match = target.name.match(/^guest-(\d+)-countryOfOriginCode$/);
    const guestIndex = match ? Number(match[1]) : 0;
    if (!guestIndex) {
      return;
    }

    const countryOfOriginInput = document.querySelector<HTMLInputElement>(
      `[name="${getGuestFieldName(guestIndex, 'countryOfOrigin')}"]`,
    );
    if (countryOfOriginInput) {
      countryOfOriginInput.value = target.value
        ? getCountryOptionsExceptFinland(currentLanguage).find((country) => country.code === target.value)?.name ?? ''
        : '';
    }
  }
});

guestFieldsContainer?.addEventListener('focusout', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.dataset.socialIdField !== 'true') {
    return;
  }

  const guestIndex = Number(target.dataset.guestIndex ?? '0');
  if (!guestIndex) {
    return;
  }

  validateFinnishSocialIdField(guestIndex, true);
});

languageButtons.forEach((button) => {
  button.addEventListener('click', () => {
    const selectedLanguage = button.dataset.language === 'fi' ? 'fi' : 'en';
    setLanguage(selectedLanguage);
  });
});

setLanguage(getStoredLanguage());
renderChannelContext();
renderButtons();
renderGuestFields();
if (!detectedChannel) {
  setInlineMessage('errors.unknownChannelHost', 'error');
}
void restoreSessionIfPossible();